import { useEffect, useState } from "react"
import { ChevronLeft, ChevronRight, Plus, Layout } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu"
import { useMeetingsStore } from "@/features/meetings/store/meetings.store"
import { useWebinarStore } from "@/features/webinar/store/webinar.store"
import type { CalendarView } from "../models/calendar.model"
import { useCalendarEvents } from "../hooks/useCalendarEvents"
import DayView from "../components/DayView"
import WeekView from "../components/WeekView"
import MonthView from "../components/MonthView"
import YearView from "../components/YearView"

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]

function getWeekRange(date: Date): string {
  const d = new Date(date)
  const day = d.getDay()
  const sun = new Date(d); sun.setDate(d.getDate() - day)
  const sat = new Date(d); sat.setDate(d.getDate() + (6 - day))
  if (sun.getMonth() === sat.getMonth()) {
    return `${MONTHS[sun.getMonth()]} ${sun.getDate()} – ${sat.getDate()}, ${sun.getFullYear()}`
  }
  return `${sun.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${sat.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${sat.getFullYear()}`
}

function getHeaderLabel(view: CalendarView, date: Date): string {
  switch (view) {
    case "day": return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    case "week": return getWeekRange(date)
    case "month": return `${MONTHS[date.getMonth()]} ${date.getFullYear()}`
    case "year": return String(date.getFullYear())
    default: return ""
  }
}

function navigateDate(view: CalendarView, date: Date, dir: -1 | 1): Date {
  const d = new Date(date)
  switch (view) {
    case "day": d.setDate(d.getDate() + dir); break
    case "week": d.setDate(d.getDate() + dir * 7); break
    case "month": d.setMonth(d.getMonth() + dir); break
    case "year": d.setFullYear(d.getFullYear() + dir); break
  }
  return d
}

function rangeFor(view: CalendarView, date: Date): { from: number; to: number } {
  const d = new Date(date)
  let from: Date, to: Date
  switch (view) {
    case "day":
      from = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
      to   = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
      break
    case "week": {
      const day = d.getDay()
      from = new Date(d); from.setDate(d.getDate() - day);       from.setHours(0, 0, 0, 0)
      to   = new Date(d); to.setDate(d.getDate() + (6 - day));   to.setHours(23, 59, 59, 999)
      break
    }
    case "month":
      from = new Date(d.getFullYear(), d.getMonth(),     1, 0, 0, 0, 0)
      to   = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999)
      break
    case "year":
      from = new Date(d.getFullYear(), 0,  1, 0, 0, 0, 0)
      to   = new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999)
      break
    default:
      from = new Date(d); to = new Date(d)
  }
  return { from: from.getTime(), to: to.getTime() }
}

const VIEWS: CalendarView[] = ["day", "week", "month", "year"]

export default function CalendarPage() {
  const t = useTranslation()
  const navigate = useNavigate()
  const today = new Date()
  const [view, setView] = useState<CalendarView>("month")
  const [currentDate, setCurrentDate] = useState<Date>(today)

  // Refresh derived statuses on mount so anything that just transitioned
  // (e.g. a meeting that started a few seconds ago) shows the right colour.
  const refreshMeetings = useMeetingsStore((s) => s.refreshStatuses)
  const refreshWebinars = useWebinarStore((s) => s.refreshStatuses)
  useEffect(() => {
    refreshMeetings()
    refreshWebinars()
  }, [refreshMeetings, refreshWebinars])

  // Fetch only meetings overlapping the visible window from the backend.
  // Result merges into the meetings store, so other views (Dashboard) keep
  // anything they already had loaded.
  const fetchRange = useMeetingsStore((s) => s.fetchRange)
  useEffect(() => {
    const { from, to } = rangeFor(view, currentDate)
    fetchRange({ from, to })
  }, [view, currentDate, fetchRange])

  // Real, store-backed events from meetings + webinars.
  const events = useCalendarEvents()

  const viewLabels: Record<CalendarView, string> = {
    day: t('calendar.day'),
    week: t('calendar.week'),
    month: t('calendar.month'),
    year: t('calendar.year'),
  }

  const goToday = () => setCurrentDate(new Date())

  return (
    <div className="p-0 landscape:p-5 w-full">
      <div className="flex h-full w-full landscape:rounded-xl shadow-xl bg-card overflow-hidden">
        {/* ── Main ── */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="shrink-0 flex items-center gap-3 px-4 py-2.5 bg-card">
            {/* Today + nav */}
            <button
              onClick={goToday}
              className="px-4 py-1.5 rounded-full border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              {t('calendar.today')}
            </button>
            <div className="flex items-center gap-0.5">
              <button
                onClick={() => setCurrentDate(navigateDate(view, currentDate, -1))}
                className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setCurrentDate(navigateDate(view, currentDate, 1))}
                className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Date label */}
            <h2 className="text-base font-medium text-foreground flex-1">
              {getHeaderLabel(view, currentDate)}
            </h2>


            {/* View selector */}
            <div className="flex items-center">
              {/* Mobile: Dropdown menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="sm:hidden w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors">
                    <Layout size={18} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {VIEWS.map(v => (
                    <DropdownMenuItem
                      key={v}
                      onClick={() => setView(v)}
                      className={`${v === "year" ? "hidden" : ""} ${view === v ? "bg-primary/10 text-primary dark:bg-primary/20" : ""}`}
                    >
                      {viewLabels[v]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Desktop: Tab buttons */}
              <div className="hidden sm:flex items-center border border-border rounded-full overflow-hidden">
                {VIEWS.map(v => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${view === v
                        ? "bg-primary/10 text-primary dark:bg-primary/20"
                        : "text-muted-foreground hover:bg-muted"
                      }`}
                  >
                    {viewLabels[v]}
                  </button>
                ))}
              </div>
            </div>
          </header>

          {/* View content */}
          <div className="flex-1 overflow-hidden relative">
            {view === "day" && <DayView currentDate={currentDate} events={events} />}
            {view === "week" && <WeekView currentDate={currentDate} events={events} onNavigateDay={(d: Date) => { setCurrentDate(d); setView("day") }} />}
            {view === "month" && <MonthView currentDate={currentDate} events={events} onNavigateDay={(d: Date) => { setCurrentDate(d); setView("day") }} />}
            {view === "year" && (
              <YearView
                currentDate={currentDate}
                events={events}
                onSelectDate={d => { setCurrentDate(d); setView("month") }}
              />
            )}

            {/* FAB */}
            <button
              onClick={() => navigate('/schedule')}
              className="absolute bottom-5 right-5 w-12 h-12 rounded-full flex items-center justify-center bg-primary text-white shadow-lg shadow-primary/40 hover:opacity-90 hover:scale-105 active:scale-95 transition-all z-40"
            >
              <Plus size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
